import { useLocation, useParams } from 'react-router';
import { useCallback, useEffect, useState, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxToolkit';
import { EditExpenseOnUpdatedFn, EditExpenseRowForm } from '../../components/forms/editExpenseRowForm';
import { MissingExpense } from '../../screens/notFound';
import { SplashScreen } from '../../screens/splashScreen';
import { SnapAPhoto, SnapAPhotoOnUploadParams } from '../../components/snapAPhoto';
import { useConfirm } from '../../providers/confirmDialog';
import { FilePreviewModal } from '../../components/filePreview';
import { createAttachment, deleteAttachment } from '../../reduxToolkit/attachmentsSlice';
import { humanize } from '../../../lib/util/string';
import { useNavigateToNewExpense } from '../../hooks/useNavigateToExpense';
import { ReceiptLibraryModal } from '../../components/receiptLibraryModal';
import { selectTodosForExpense } from '../../reduxToolkit/selectors/todos';
import { updateExpense } from '../../reduxToolkit/expensesSlice';

import './editExpense.scss'
import { BlobModel } from '../../reduxToolkit/blobsSlice';

export function EditExpense() {
  const params = useParams()
  const navigateToNewExpense = useNavigateToNewExpense()
  const location = useLocation()
  const [confirm] = useConfirm()
  const dispatch = useAppDispatch()

  const validateOnMount = location.state?.validateOnMount
  const disabledFields = location.state?.disabledFields

  const expense = useAppSelector((s) => s.expenses.expenses?.find((e) => e.id == params.expenseId))
  const attachments = useAppSelector((s) => s.attachments.attachments?.filter((a) =>
    a.record_id == expense?.id && a.table_name == 'expenses')) || []
  const expenseTodos = useAppSelector(selectTodosForExpense(params.expenseId || ''))
  const allBlobs = useAppSelector((s) => s.blobs.blobs)

  // Get existing attachment blobs from Redux instead of using useLoadBlobs
  const existingAttachmentBlobs = useMemo(() => {
    const blobKeys = attachments.map((a) => a.blob_key)
    return allBlobs.filter((b) => blobKeys.includes(b.key))
  }, [allBlobs, attachments])
  
  const onSubmit: EditExpenseOnUpdatedFn = (expense) => {
    // Attachments are created and removed as soon as the user acts on them (see
    // onUpload / onDeleteBlob), so Save only has to persist the expense fields.
    navigateToNewExpense(expense)
  }

  // Sync can still be delivering this expense. Wait the same second ExpenseRedirect
  // waits before telling the member it is gone. Skip the timer when the expense
  // is already here so a test or storybook page does not set state after teardown.
  const [gaveUpWaiting, setGaveUpWaiting] = useState(false)
  useEffect(() => {
    if (expense) {
      return
    }
    const timeout = setTimeout(() => setGaveUpWaiting(true), 1000)
    return () => clearTimeout(timeout)
  }, [expense])

  const [previewFile, setPreviewFile] = useState<BlobModel | null>(null)
  const [previewAttachment, setPreviewAttachment] = useState<any | null>(null)
  const [showReceiptLibraryModal, setShowReceiptLibraryModal] = useState(false)

  // "Upload New" has already written the blob to storage by the time this fires, so
  // link it to the expense immediately rather than waiting for Save. Deferring meant a
  // member who uploaded and then left the screen (a natural thing to do once the file
  // shows up under Attachments) silently lost the attachment, and the orphaned blob
  // turned up unattached in their attachments instead.
  const onUpload = useCallback(async (params: SnapAPhotoOnUploadParams) => {
    if (!expense) { return }

    const now = new Date().toISOString()
    dispatch(createAttachment({
      id: uuidv4(),
      blob_key: params.blob.key,
      created_at: now,
      membership_id: expense.membership_id,
      record_id: expense.id,
      table_name: 'expenses',
      updated_at: now,
      purpose: params.purpose || 'receipt',
    }))
  }, [dispatch, expense])

  const onDeleteBlob = useCallback((blobKey: string) => {
    const attachment = attachments?.find((a) => a.blob_key === blobKey)
    if (!attachment) { return }

    const now = new Date().toISOString()
    dispatch(deleteAttachment({
      id: attachment.id,
      updated_at: now,
      deleted_at: now,
    }))
  }, [attachments, dispatch])

  const onReceiptLibraryAttached = useCallback((_blobs: BlobModel[]) => {
    // Receipts are attached via Redux actions in the modal, so just close it -- the
    // attachments show up in the list above on the next render.
    setShowReceiptLibraryModal(false)
  }, [])
  
  // Check if there's an itemized-bill TODO for this expense
  const hasItemizedBillTodo = expenseTodos.some((todo) => todo.todo_type === 'itemized-bill')

  // Check if there's a duplicate-detected TODO for this expense
  const hasDuplicateTodo = expenseTodos.some((todo) => todo.todo_type === 'duplicate-detected')

  const handleMarkAsNotDuplicate = useCallback(() => {
    dispatch(updateExpense({
      id: expense!.id,
      is_marked_not_duplicate: true,
      updated_at: new Date().toISOString()
    }))
  }, [dispatch, expense])

  if (!expense) {
    if (!gaveUpWaiting) {
      return <SplashScreen />
    }
    return <MissingExpense />
  }

  return <div className="edit-expense">
    <div className="row">
      <div className="col-12 col-md-8">
        <h1 className="edit-expense__title">{expense.is_draft === true ? 'Create Expense' : 'Edit Expense'}</h1>
        <p className="edit-expense__subtitle">
          Enter the details of your expense below.
          You can also upload an itemized bill to attach it to this expense.
        </p>

        {hasDuplicateTodo && (
          <div className="alert alert-warning d-flex align-items-center justify-content-between mb-3">
            <div>
              <i className="material-icons me-2" style={{ verticalAlign: 'middle' }}>content_copy</i>
              <span>This expense may be a duplicate. If it's not a duplicate, you can dismiss this warning.</span>
            </div>
            <button
              className="btn btn-outline-warning btn-sm"
              onClick={handleMarkAsNotDuplicate}
            >
              Not a Duplicate
            </button>
          </div>
        )}

        <EditExpenseRowForm
            expense={expense}
            onSubmit={onSubmit}
            validateOnMount={validateOnMount}
            disabledFields={disabledFields}>

          <div className='row edit-expense__attachments mt-4'>
            <div className='col-12'>
              <h3>Attachments</h3>
              {existingAttachmentBlobs && existingAttachmentBlobs.length > 0 &&
                <div className='row edit-expense__attachments-header'>
                  <div className='col-7'>
                    Name
                  </div>
                  <div className='col-3'>
                    Purpose
                  </div>
                  <div className='col-2 d-flex justify-content-end'>
                    &nbsp;
                  </div>
                </div>
              }
              {(attachments || [])
                  .map((attachment) => {
                    const blob = existingAttachmentBlobs?.find((b) => b.key === attachment.blob_key)
                    if (!blob) {
                      return null
                    }
                    
                    return <div className='row edit-expense__attachments-row' key={blob.key}>
                      <div className='col-7 d-flex align-items-center'>
                        {contentTypeIcon(blob.content_type)}
                        <span className='edit-expense__attachments-row-name text-truncate me-2'>{blob.file_name}</span>
                      </div>
                      <div className='col-3'>
                        {attachment.purpose ? humanize(attachment.purpose) : ''}
                      </div>
                      <div className='col-2 d-flex justify-content-end'>
                        <button className='btn btn-link' onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          setPreviewFile(blob)
                          setPreviewAttachment(attachment)
                        }}>
                          <i className='material-icons'>open_in_new</i>
                        </button>
                        <button className='btn btn-link'
                            onClick={() => {
                              confirm({
                                message: 'Are you sure you want to delete this attachment?'
                              }).then((confirmed) => {
                                if (confirmed) {
                                  onDeleteBlob(blob.key)
                                }
                              })
                            }}>
                          <i className='material-icons'>delete</i>
                        </button>
                      </div>
                    </div>
                  })}

              <div className="row mt-2">
                <div className="col-12 d-flex gap-2 justify-content-center">
                  <SnapAPhoto onUpload={onUpload} saveToReceiptLibrary={true} showPurposeSelection={true}>
                    <button type='button' className='btn btn-outline-primary'>+ Upload New</button>
                  </SnapAPhoto>
                  <button
                    type='button'
                    className='btn btn-outline-secondary'
                    onClick={() => setShowReceiptLibraryModal(true)}
                  >
                    <i className='material-icons me-1'>receipt</i>
                    Attach from Library
                  </button>
                </div>
              </div>
            </div>
          </div>
        </EditExpenseRowForm>
      </div>
    </div>
    
    <FilePreviewModal 
      show={!!previewFile} 
      fileOrBlob={previewFile} 
      attachment={previewAttachment}
      onClose={() => {
        setPreviewFile(null)
        setPreviewAttachment(null)
      }} 
    />
    
    <ReceiptLibraryModal
      isOpen={showReceiptLibraryModal}
      onClose={() => setShowReceiptLibraryModal(false)}
      onAttached={onReceiptLibraryAttached}
      expenseId={expense.id}
      initialFilter={hasItemizedBillTodo ? 'itemized-bill' : 'all'}
    />
  </div>
} 

function contentTypeIcon(contentType: string) {
  if (contentType.startsWith('image/')) {
    return <i className='material-icons'>image</i>
  }
  // pdf
  if (contentType.startsWith('application/pdf')) {
    return <i className='material-icons'>picture_as_pdf</i>
  }
  
  return <i className='material-icons'>file_present</i>
}
